import { z } from "zod";
import { ConfigKindSchema, QueueSnapshotMetricsSchema, type QueueSnapshotMetrics } from "./rewind.js";

/**
 * ADR-0016 (#231): the central rows behind Rewind and the shapes its routes
 * answer with. Counts and configuration only - tier 2 under ADR-0006. Agent
 * names appear only in `RewindAgentsResponse`, which is rendered and never
 * stored.
 */

/** `QueueSnapshots` - PK `environment_id`, SK `${minute}#${chunk}`. */
export const QueueSnapshotRecordSchema = z.object({
  environment_id: z.string().min(1),
  sk: z.string().min(1),
  minute: z.string(),
  queues: z.record(z.string(), QueueSnapshotMetricsSchema),
  chunk_count: z.number().int().positive(),
  received_at: z.string(),
  ttl: z.number().int().positive(),
});
export type QueueSnapshotRecord = z.infer<typeof QueueSnapshotRecordSchema>;

/** `ConfigHistory` history row - PK `${environment_id}#${kind}#${id}`, SK `observed_at`. `config` is the `config_version` item's own config. */
export const ConfigHistoryRecordSchema = z.object({
  pk: z.string().min(1),
  /** The table's sort key: `observed_at` for a version. */
  sk: z.string().min(1),
  observed_at: z.string(),
  environment_id: z.string().min(1),
  kind: ConfigKindSchema,
  id: z.string().min(1),
  name: z.string(),
  config: z.unknown().optional(),
  hash: z.string(),
  deleted: z.literal(true).optional(),
  ttl: z.number().int().positive(),
});
export type ConfigHistoryRecord = z.infer<typeof ConfigHistoryRecordSchema>;

/** `ConfigHistory` catalog row - PK `${environment_id}#catalog`, SK `${kind}#${id}`: the latest hash and the last push that carried it. */
export const ConfigCatalogRecordSchema = z.object({
  pk: z.string().min(1),
  /** `${kind}#${id}`. */
  sk: z.string().min(1),
  environment_id: z.string().min(1),
  kind: ConfigKindSchema,
  id: z.string().min(1),
  name: z.string(),
  hash: z.string(),
  last_push_at: z.string(),
  deleted_at: z.string().optional(),
});
export type ConfigCatalogRecord = z.infer<typeof ConfigCatalogRecordSchema>;

export const configHistoryKey = (environmentId: string, kind: string, id: string) => `${environmentId}#${kind}#${id}`;
export const configCatalogKey = (environmentId: string) => `${environmentId}#catalog`;
/** The catalog row's sort key; the table's keys are `pk` / `sk` for both kinds of row. */
export const configCatalogSort = (kind: string, id: string) => `${kind}#${id}`;

/** `ContactDailyCounts` attribute for the staffing-gap minutes of one queue in one UTC hour. */
export const gapAttribute = (queueId: string, hour: number) => `gap#${queueId}#${String(hour).padStart(2, "0")}`;
export function parseGapAttribute(name: string): { queueId: string; hour: number } | undefined {
  const m = /^gap#(.+)#(\d{2})$/.exec(name);
  return m ? { queueId: m[1]!, hour: Number(m[2]) } : undefined;
}

/** A minute with contacts waiting and no agent available - the staffing-gap rule, one definition for the rollup and the drill. */
export const isGapMinute = (m: Pick<QueueSnapshotMetrics, "contacts_in_queue" | "agents_available">) => m.contacts_in_queue > 0 && m.agents_available === 0;

// ---------------------------------------------------------------- API

export const RewindCauseKindSchema = z.enum(["misremembered", "closed", "unstaffed", "misrouted", "offered_and_missed", "staffed_but_unavailable", "overloaded", "undetermined"]);
export type RewindCauseKind = z.infer<typeof RewindCauseKindSchema>;

export const RewindSourceSchema = z.enum(["snapshot", "contacts", "configuration", "agent_events"]);
export type RewindSource = z.infer<typeof RewindSourceSchema>;

export interface RewindSourceVerdict {
  source: RewindSource;
  status: "agrees" | "disagrees" | "missing";
  detail: string;
}

export interface RewindCause {
  cause: RewindCauseKind;
  evidence: string[];
  sources: RewindSourceVerdict[];
}

export interface RewindMinute {
  minute: string;
  /** The snapshot's counts for the queue; absent when the minute was missed. */
  snapshot?: QueueSnapshotMetrics;
  missed: boolean;
  /** Contacts in the queue at the minute by their records (enqueue <= minute < dequeue or disconnect). */
  derived: number;
  /** The snapshot has shown more waiting than the records for five minutes running: contacts that overflowed to another queue. */
  overflow: boolean;
}

export interface RewindProfile {
  id: string;
  name: string;
  channel: string;
  priority: number;
  delaySeconds: number;
  /** How many contacts of this channel an agent on the profile takes at once. */
  concurrency?: number;
  observedAt: string;
}

export interface RewindConfiguration {
  at: string;
  queue?: { id: string; name: string; hoursOfOperationId?: string; maxContacts?: number; status?: string; observedAt: string };
  hours?: { id: string; name: string; timezone: string; openAtEnqueue: boolean; observedAt: string };
  profiles: RewindProfile[];
  /** False when no configuration version was on file at the minute - the stack may not send it yet. */
  available: boolean;
}

export interface RewindContact {
  contactId: string;
  /** The queue the record names - the last one the contact was in. */
  queueName?: string;
  enqueuedAt: string;
  dequeuedAt?: string;
  waitSeconds?: number;
  disconnectReason?: string;
}

export interface RewindResponse {
  queue: { id: string; name?: string };
  from: string;
  to: string;
  minutes: RewindMinute[];
  coverage: { observed: number; missed: number };
  /** The contact the rewind was opened from, when one was named. */
  contact?: RewindContact;
  /** Who was waiting at `from`, by their records - at most 100, oldest first. */
  waiting: Array<{ contactId: string; enqueuedAt: string }>;
  configuration: RewindConfiguration;
  cause: RewindCause;
  /** Whether the agents at a minute can be read: the stack keeps agent events and reported where. */
  agentHistory: boolean;
}

export interface RewindAgentContact {
  contactId?: string;
  state: string;
  channel?: string;
  since?: string;
}

export interface RewindAgent {
  /** Rendered for the person looking; never stored at Gairm. */
  name: string;
  username?: string;
  status: string;
  statusType?: string;
  since?: string;
  routingProfile?: string;
  contacts: RewindAgentContact[];
}

export interface RewindAgentsResponse {
  queueId: string;
  at: string;
  agents: RewindAgent[];
  /** With `contact`: the agents whose events show that contact missed or rejected. */
  missedBy: Array<{ name: string; state: string; at?: string }>;
  read: { objects: number; bytes: number; truncated: boolean };
}

export interface StaffingGapQueue {
  queueId: string;
  name?: string;
  /** Minutes in the window with contacts waiting and no agent available. */
  total: number;
  /** The same, per UTC hour of day, 0-23. */
  byHour: number[];
  /** Each UTC day and hour that had any, for the drill to its minutes. */
  cells: Array<{ date: string; hour: number; minutes: number }>;
}

export interface StaffingGapsResponse {
  days: number;
  from: string;
  to: string;
  queues: StaffingGapQueue[];
  /** False when no snapshot has landed for the environment in the window. */
  available: boolean;
}

export interface StaffingGapMinutesResponse {
  queueId: string;
  date: string;
  hour: number;
  minutes: Array<{ minute: string; contactsInQueue: number; oldestContactAgeSeconds: number; agentsOnline: number; agentsStaffed: number }>;
  /** Minutes of the hour with no snapshot at all. */
  missed: string[];
}
